import React, { FC } from 'react';
import { Row, Col, CardDeck, Card, CardBody, CardText, CardFooter } from 'reactstrap';
import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import LeadDropDownloadBtn from '@/libs/LeadDrop/LeadDrop';
import styles from './FindMore.module.scss';
import useSWRInfinite, { SWRInfiniteKeyLoader } from 'swr/infinite';
import { fetcher } from '@/libs/fetcher/fetcher';
import TruncateMarkup from 'react-truncate-markup';
import parse from 'html-react-parser';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from './FindMoreSkeleton';

//import components
import GridImages from '@/components/GridImages/GridImages';

// Context
import { useResourceContext } from '@/context/resourceContext';

//Import Libs
import { ClientData } from '@/libs/interfaceProps/clientListProps';
import { useLangContext } from '@/context/langContext';
import { fulfillmentPageCompsProps } from '@/libs/interfaceProps/fulfillmentPageCompsProps';
import { sanitizeTitle } from '@/libs/sanitizeTitle/sanitizeTitle';
import useResFallbackImages from '@/libs/resFallbackImages/resFallbackImages';
import { sitenameImageIndexHandler } from '@/libs/sitenameImageIndexHandler/sitenameImageIndexHandler';
import { pushGTMClickTags } from '@/libs/pushGTMTags/pushGTMTags';
const defaultSize = 3;
const FindMore: FC<fulfillmentPageCompsProps> = ({
  params,
  queryParams
}) => {
  const {
    resource
  } = useResourceContext();
  const {
    lang
  } = useLangContext();
  const t = useTranslations('Fulfillment');
  const leadDropDownloadHandler = LeadDropDownloadBtn(params, queryParams);
  const fallbackImages = useResFallbackImages();
  const sitenameImageIndex: Record<string, number> = {};
  const getKey: SWRInfiniteKeyLoader = (pageIndex: number) => {
    // PageIndex will always start at 0, set start as 0.
    let start;
    if (pageIndex === 0) {
      start = 0;
    } else {
      start = pageIndex * defaultSize;
    }
    const thisResId = resource?.resId;
    return `/hub/services/findMore?lang=${lang ? lang : 'en'}&resId=${thisResId}&start=${start}&size=${defaultSize}`;
  };
  const {
    data,
    error,
    size,
    setSize
  } = useSWRInfinite(getKey, fetcher);
  if (error) return null;
  if (!data) return null;
  const castTypeData: any = data;
  const orgData: ClientData[] = [];
  const foundCount: any = [];
  if (data && data?.[0]?.found !== 0) {
    const orgDataAndCount = castTypeData.map((singleArray: any, index: number) => {
      if (!singleArray?.error) {
        // on the first index find the total count
        if (index === 0) {
          foundCount['found'] = singleArray.found;
        }
        // push each array after each click into 1 single array, so we can now use to display data.
        // The reason we do this is to make it easier to read 1 single array rather than multiple arrays.
        orgData.push(singleArray?.body);
      }
    });
    const clientData: ClientData[] = orgDataAndCount ? orgData.flat(1) : [];
    const totalCount = clientData.length;
    const isReachedEnd = totalCount >= foundCount['found'];
    if (!data?.[0].error && data !== undefined) {
      return <>
     <div className={'section-dividers'}>
      <hr />
     </div>

     <InfiniteScroll className={'infinite-scroll'} dataLength={totalCount} next={() => setSize(size + 1)} hasMore={!isReachedEnd} loader={<Skeleton />}>
      <Row className={'gx-'}>
       <h2 className={'section-heading'} data-bdd={'section-heading_top-picks'}>
        {t('FindMoreContent')}
       </h2>
      </Row>

      <Row className={classnames(styles['findmore-row'], 'g-3', 'mt-0')} data-bdd="findmore-row">
       {clientData?.map(({
              id,
              bestTitle,
              docType,
              shortDescription,
              resId,
              orgName,
              hasVendorQuestions,
              primarySiteName
            }, index) => <Col sm="12" md="12" lg="4" key={`findmoreitem-` + id + index}>
          <CardDeck className={styles['card-decks']}>
           <Card className={styles['findmore-card']}>
            <div className={classnames('faux-letterbox', 'grid-image-wrap')}>
             <a className="asset-link" onClick={e => {
                      leadDropDownloadHandler(e, resId, hasVendorQuestions);
                      pushGTMClickTags({});
                    }} data-bdd={`findmore-link-${resId}`}>
              <GridImages GridType={'findmore'} resId={resId} primarySiteName={primarySiteName ? primarySiteName : 'genericMulti'} fallbackimage={sitenameImageIndexHandler(sitenameImageIndex, fallbackImages, primarySiteName ? primarySiteName : 'genericMulti')} />
             </a>
            </div>

            <CardBody className={styles['tt-card-body']}>
             {/*  Trucate module expects a strict string passed, only display if var is string and has length */}
             {typeof bestTitle === 'string' && bestTitle.length > 0 && <a className="asset-link" onClick={e => {
                      leadDropDownloadHandler(e, resId, hasVendorQuestions);
                      pushGTMClickTags({});
                    }}>
               <TruncateMarkup lines={2} tokenize={'words'}>
                <h3 className={styles['findmore-card-title']}>
                 {parse(sanitizeTitle(bestTitle))}
                </h3>
               </TruncateMarkup>
              </a>}

             {typeof shortDescription === 'string' && shortDescription.length > 0 && <TruncateMarkup lines={3} tokenize={'words'}>
                <p>{parse(sanitizeTitle(shortDescription))}</p>
               </TruncateMarkup>}
            </CardBody>

            <div className={styles['bottom-block']}>
             <CardText className={'asset-type'}>{docType}</CardText>

             <CardText className={'org-name'}>
              {t('By')} <span>{orgName}</span>
             </CardText>

             <CardFooter className={styles['findmore-footer']}>
              <a className={'tt-button-med'} onClick={e => {
                        leadDropDownloadHandler(e, resId, hasVendorQuestions);
                        pushGTMClickTags({
                          click_element: 'Anchor',
                          click_closest_class: 'NONE',
                          click_section_ID: 'NONE',
                          link_classes: 'tt-button-med',
                          link_id: 'NONE',
                          link_text: 'Download',
                          link_url: `/hub/asset/${resId}`,
                          click_category: 'NONE',
                          click_destination: 'Download asset',
                          click_closest_href: 'NONE'
                        });
                      }}>
               {t('Download')}
              </a>
             </CardFooter>
            </div>
           </Card>
          </CardDeck>
         </Col>)}
      </Row>
     </InfiniteScroll>
    </>;
    }
  }
  return null;
};
export default FindMore;